import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { StatusEnum } from '../../../../common/data/base/enums/statusEnum';
import MESSAGES from '../../../../common/data/constants/messages';
import { IEmployee } from '../../../../common/data/dtos/employee';
import { ITraining } from '../../../../common/data/dtos/training';
import { ITrainingEmployee } from '../../../../common/data/dtos/training-employee';
import TrainingEmployeeStatusEnum from '../../../../common/data/enums/enumTrainingEmployeeStatus';
import Button from '../../../../components/bootstrap/Button';
import Card, { CardFooterRight, CardTabItem } from '../../../../components/bootstrap/Card';
import FormGroup from '../../../../components/bootstrap/forms/FormGroup';
import InputGroup from '../../../../components/bootstrap/forms/InputGroup';
import Select from '../../../../components/bootstrap/forms/Select';
import Modal, { ModalBody, ModalFooter, ModalHeader, ModalTitle } from '../../../../components/bootstrap/Modal';
import Spinner from '../../../../components/bootstrap/Spinner';
import { showSuccess, showWarning } from '../../../../components/extras/Notifications';
import trainingEmployeeService from '../../../../services/training-employee-service';
import trainingService from '../../../../services/training-service';

type TrainingEmployeeFormValues = {
    trainingId: string;
};

interface Props {
    isModalOpened: boolean;
    selectedEmployee: IEmployee | null;
    onModalClose: () => void;
    onAfterSubmit: () => void;
}

const TrainingEmployeeModal = ({ isModalOpened, selectedEmployee, onModalClose, onAfterSubmit }: Props) => {
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [trainings, setTrainings] = useState<ITraining[]>([]);
    const [trainingsForEmployee, setTrainingsForEmployee] = useState<ITrainingEmployee[]>([]);

    const formik = useFormik({
        initialValues: {
            trainingId: '',
        },

        validate: (values) => {
            const errors: {
                trainingId?: string;
            } = {};

            if (!values.trainingId) errors.trainingId = MESSAGES.VALIDATION.REQUIRED_FIELD;

            return errors;
        },

        onSubmit: (values, { resetForm }) => {
            handleAddClick(values);
            resetForm();
        },
    });

    useEffect(() => {
        if (selectedEmployee) {
            resetForm();
            fetchData();
        }
    }, [isModalOpened]);

    const fetchData = async () => {
        setIsSubmitting(true);

        try {
            if (!selectedEmployee?.id) {
                showWarning(MESSAGES.VALIDATION.PARAMETERS_ERROR_VALIDATION);
            } else {
                const [trainingsResponse, trainingEmployeeResponse] = await Promise.all([trainingService.getTrainings(), trainingEmployeeService.getTrainingEmployeeByEmployee(selectedEmployee.id)]);

                if (trainingsResponse.error) {
                    showWarning(trainingsResponse.error);
                    return;
                }

                if (trainingEmployeeResponse.error) {
                    showWarning(trainingEmployeeResponse.error);
                    return;
                }

                setTrainings(trainingsResponse.trainings || []);
                setTrainingsForEmployee(trainingEmployeeResponse.trainingEmployees || []);
            }
        } catch (error) {
            console.error('Erro nas requisições:', error);
            showWarning('Erro ao carregar dados');
        } finally {
            setIsSubmitting(false);
        }
    };

    const fetchTrainingEmployees = async () => {
        if (selectedEmployee?.id) {
            const response = await trainingEmployeeService.getTrainingEmployeeByEmployee(selectedEmployee?.id);

            if (response.error) {
                showWarning(response.error);
                return;
            }

            setTrainingsForEmployee(response.trainingEmployees || []);
        }
    };

    const onFormSubmit = async () => {
        if (!trainingsForEmployee) {
            showWarning(MESSAGES.VALIDATION.EMPTY_COLLECTION);
            return;
        }

        const itensToAdd = trainingsForEmployee.filter((item) => !item.id || item.id.trim() === '').map(({ training, ...toAdd }) => toAdd);

        if (!itensToAdd || itensToAdd.length === 0) {
            showWarning(MESSAGES.VALIDATION.EMPTY_COLLECTION_TO_ADD);
            return;
        }

        setIsSubmitting(true);

        const response = await trainingEmployeeService.createManyTrainingEmployee(itensToAdd);

        setIsSubmitting(false);

        if (response.error) {
            showWarning(MESSAGES.ACTION.SAVE_ERROR);
            return;
        }

        showSuccess(MESSAGES.ACTION.SAVED);
        resetForm();
        onAfterSubmit();
    };

    const resetForm = () => {
        formik.resetForm({
            values: {
                trainingId: '',
            },
        });
        setTrainingsForEmployee([]);
    };

    const onClose = () => {
        onModalClose();
    };

    const handleAddClick = async (values: TrainingEmployeeFormValues) => {
        if (!selectedEmployee?.id || !values.trainingId) {
            showWarning(MESSAGES.VALIDATION.PARAMETERS_ERROR_VALIDATION);
            return;
        }

        const exists = trainingsForEmployee.some((item) => item.trainingId === values.trainingId);
        if (exists) {
            showWarning(MESSAGES.VALIDATION.ITEM_EXISTS);
        } else {
            const selectedTraining = trainings.find((p) => p.id === values.trainingId);

            const newTrainingEmployee: ITrainingEmployee = {
                employeeId: selectedEmployee?.id,
                doneDate: undefined,
                trainingId: values.trainingId,
                rowVersion: 1,
                status: StatusEnum.Active,
                training: selectedTraining,
                situation: TrainingEmployeeStatusEnum.pending,
            };

            setTrainingsForEmployee((prev) => [...prev, newTrainingEmployee]);
        }
    };

    const handleDeleteClick = async (trainingEmployee: ITrainingEmployee) => {
        if (!trainingEmployee.id) {
            const filteredList = trainingsForEmployee.filter((item) => item.trainingId !== trainingEmployee.trainingId);
            setTrainingsForEmployee(filteredList);
        } else {
            setIsSubmitting(true);

            const response = await trainingEmployeeService.deleteTrainingEmployee(trainingEmployee.id);

            setIsSubmitting(false);

            if (response.error) {
                showWarning(response.error);
                return;
            }

            showSuccess(MESSAGES.ACTION.DELETED);
            fetchTrainingEmployees();
        }
    };

    return (
        <Modal isOpen={isModalOpened} setIsOpen={onClose} size="xl" isCentered isStaticBackdrop>
            <ModalHeader setIsOpen={onClose}>
                <ModalTitle id="work-resource-employee-modal">Manutenção de treinamentos do colaborador</ModalTitle>
            </ModalHeader>
            <ModalBody>
                <Card hasTab tabButtonColor="primary">
                    <CardTabItem id="tab-item-1" title={selectedEmployee?.name || 'Manutenção de treinamentos do colaborador'} icon="PeopleAlt">
                        <div className="row g-4">
                            <div>
                                <InputGroup isWrap>
                                    <FormGroup id="trainingId" label="Treinamento" isFloating>
                                        <Select
                                            ariaLabel="Escolha um Treinamento"
                                            list={[
                                                {
                                                    value: '',
                                                    text: 'Selecione um Treinamento',
                                                },
                                                ...trainings.map((training) => ({
                                                    value: training.id,
                                                    text: `${training.name}`,
                                                })),
                                            ]}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.trainingId}
                                            isValid={formik.isValid}
                                            isTouched={formik.touched.trainingId}
                                            invalidFeedback={formik.errors.trainingId}
                                            validFeedback=""
                                        />
                                    </FormGroup>
                                    <Button icon="AddCircleOutline" color="primary" onClick={formik.handleSubmit}>
                                        Adicionar
                                    </Button>
                                </InputGroup>
                            </div>

                            <div className="col-lg-12 col-md-12 col-sm-12 table-responsive ">
                                <table className="table table-modern table-hover">
                                    <thead>
                                        <tr>
                                            <th>Treinamento</th>
                                            <th>Validade</th>
                                            <td aria-labelledby="Actions" />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {trainingsForEmployee
                                            // .filter((item) => item.situation === TrainingEmployeeStatusEnum.pending)
                                            .map((item: ITrainingEmployee) => (
                                                <tr key={item.id}>
                                                    <td>{item.training?.name}</td>
                                                    <td>{item.training?.validity || '-'}</td>
                                                    <td className="text-end">
                                                        <Button color="dark" isLight icon="Delete" onClick={() => handleDeleteClick(item)} aria-label="Remover" />
                                                    </td>
                                                </tr>
                                            ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </CardTabItem>
                </Card>
            </ModalBody>
            <ModalFooter className="bg-transparent">
                <CardFooterRight>
                    <Button color="success" className="w-100" onClick={onFormSubmit} isDisable={isSubmitting}>
                        {isSubmitting && <Spinner isSmall inButton isGrow />}
                        Salvar
                    </Button>
                </CardFooterRight>
            </ModalFooter>
        </Modal>
    );
};

export default TrainingEmployeeModal;
